import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { Store } from '@ngrx/store';
import { KeycloakService } from 'keycloak-angular';
import { DateTime } from 'luxon';

import { AnalyticsService } from '@core/analytics/analytics.service';
import { TagEvent } from '@core/analytics/enums/tag-event.enum';
import { PostHogLogService } from '@core/analytics/posthog-log.service';
import { PostHogService } from '@core/analytics/posthog.service';
import { getImpersonationTokenClaims } from '@core/helpers/impersonation-token-claims';
import { clearPersistedImpersonation, getPersistedImpersonation } from '@core/helpers/impersonation-token-persistence';
import { isKioskUrl } from '@core/helpers/is-kiosk-url';
import { clearKioskPersistedTokens, getKioskPersistedTokens, persistKioskTokens } from '@core/helpers/kiosk-token-persistence';
import { clearPersistedTokens, getPersistedTokens, persistTokens } from '@core/helpers/token-persistence';
import { logout, setInitialTokenAndPermissions } from '@core/stores/auth/auth.actions';
import { CONFIG_TOKEN } from '@core/tokens';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';

import { isDefined } from '@shared/utils/is-defined';
import { getRefreshTokenExpiresDateTime } from '@shared/utils/refresh-token-expires';

export const keycloakInitializer: EnvironmentProviders = provideAppInitializer(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async () => {
    const keycloak = inject(KeycloakService);
    const config = inject(CONFIG_TOKEN);
    const store = inject(Store);
    const analyticsService = inject(AnalyticsService);
    const posthogService = inject(PostHogService);
    const posthogLogService = inject(PostHogLogService);

    posthogService.init();
    posthogLogService.info('frontend application initialized', { runtime: 'browser' });

    const isKiosk = isKioskUrl();
    const clientId = isKiosk ? config.kioskKeycloak.clientId : config.keycloak.clientId;

    const initOptions: Keycloak.KeycloakInitOptions = {
      pkceMethod: false,
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/keycloak/silent-check-sso.html`,
    };

    let savedToken: string | null = null;
    let savedRefreshToken: string | null = null;
    let savedIdToken: string | null = null;
    let savedTimeSkew: number | null = null;
    if (isKiosk) {
      const { token, refreshToken, idToken, timeSkew } = getKioskPersistedTokens();
      savedToken = token;
      savedRefreshToken = refreshToken;
      savedIdToken = idToken;
      savedTimeSkew = timeSkew;
    } else {
      const { token, refreshToken, idToken, timeSkew } = getPersistedTokens();
      savedToken = token;
      savedRefreshToken = refreshToken;
      savedIdToken = idToken;
      savedTimeSkew = timeSkew;
    }

    if (savedToken && savedRefreshToken) {
      initOptions.token = savedToken;
      initOptions.refreshToken = savedRefreshToken;
      if (savedIdToken) initOptions.idToken = savedIdToken;
      if (savedTimeSkew !== null) initOptions.timeSkew = savedTimeSkew;
    }

    await keycloak.init({
      config: {
        url: config.keycloak.url,
        realm: config.keycloak.realm,
        clientId,
      },
      initOptions,
    });

    let initialToken: string | null = null;
    let refreshTokenExpires: DateTime | null = null;

    if (savedToken && savedRefreshToken) {
      try {
        initialToken = await keycloak.getToken();
        refreshTokenExpires = getRefreshTokenExpiresDateTime(keycloak);
      } catch (error) {
        console.error(error);
      }
    } else {
      let existingTokenInKeycloak: string | null = null;
      try {
        existingTokenInKeycloak = await keycloak.getToken();
      } catch (error) {
        console.error(error);
      }

      if (existingTokenInKeycloak) {
        const otherModeHasTokens = isKiosk ? getPersistedTokens().token !== null : getKioskPersistedTokens().token !== null;

        if (otherModeHasTokens) {
          if (isKiosk) {
            clearPersistedTokens();
            clearPersistedImpersonation();
          } else {
            clearKioskPersistedTokens();
          }
          keycloak.logout();
          return;
        } else {
          initialToken = existingTokenInKeycloak;
          refreshTokenExpires = getRefreshTokenExpiresDateTime(keycloak);
        }
      }
    }

    if (initialToken) {
      const instance = keycloak.getKeycloakInstance();

      if (isKiosk) {
        if (instance.token && instance.refreshToken && instance.idToken && isDefined(instance.timeSkew)) {
          persistKioskTokens(instance.token, instance.refreshToken, instance.idToken, instance.timeSkew);
        }

        instance.onAuthRefreshSuccess = () => {
          if (instance.token && instance.refreshToken && instance.idToken && isDefined(instance.timeSkew)) {
            persistKioskTokens(instance.token, instance.refreshToken, instance.idToken, instance.timeSkew);
          }
        };
      } else {
        if (instance.token && instance.refreshToken && instance.idToken && isDefined(instance.timeSkew)) {
          persistTokens(instance.token, instance.refreshToken, instance.idToken, instance.timeSkew);
        }

        instance.onAuthRefreshSuccess = () => {
          if (instance.token && instance.refreshToken && instance.idToken && isDefined(instance.timeSkew)) {
            persistTokens(instance.token, instance.refreshToken, instance.idToken, instance.timeSkew);
          }
        };
      }

      instance.onAuthRefreshError = () => {
        store.dispatch(logout());
      };

      const permissions = keycloak.getUserRoles(false) as PermissionStoreModelEnum[];
      const tokenParsed = instance.tokenParsed;
      const name = instance.tokenParsed?.['preferred_username'];
      const persistedImpersonation = isKiosk ? undefined : getPersistedImpersonation();
      const impersonationClaims = persistedImpersonation?.accessToken
        ? getImpersonationTokenClaims(persistedImpersonation.accessToken)
        : undefined;
      const impersonation =
        impersonationClaims && persistedImpersonation?.accessToken && persistedImpersonation.targetUserId
          ? { token: persistedImpersonation.accessToken, targetUserId: persistedImpersonation.targetUserId, ...impersonationClaims }
          : undefined;

      if (persistedImpersonation && !impersonation) {
        clearPersistedImpersonation();
      }

      const userId = tokenParsed?.['sub'];
      if (userId) {
        const username = name;
        const organisationId = tokenParsed?.['organization']?.[0];

        posthogService.posthog.identify(userId, {
          ...(username ? { username } : {}),
          ...(organisationId ? { organisation_id: organisationId } : {}),
        });
      }

      store.dispatch(
        setInitialTokenAndPermissions({
          token: initialToken,
          refreshTokenExpiresTimestamp: refreshTokenExpires && refreshTokenExpires.toISO(),
          permissions,
          name,
          organisationId: tokenParsed?.['organization']?.[0],
          impersonation,
        })
      );
      analyticsService.pushEvent({
        event: TagEvent.Login,
        user_id: tokenParsed?.['sub'] || '',
        organization: tokenParsed?.['organization']?.[0] || '',
        roles: permissions.join('|'),
      });
    } else {
      try {
        if (isKiosk) {
          clearKioskPersistedTokens();
        } else {
          clearPersistedTokens();
          clearPersistedImpersonation();
        }

        keycloak.login({
          redirectUri: window.location.href,
        });
      } catch (error) {
        console.error(error);
      }
    }
  }
);
